import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { supabase } from '../lib/supabase';
import { useMyRestaurants } from '../hooks/useMyRestaurants';
import { formatMoney, parseMoneyToCents, pickLocale } from '../lib/format';
import { CATEGORY_BANK, categoryName } from '../lib/categories';

type I18nText = Record<string, string>;
const ALL_LANGS = ['pt-BR', 'en', 'es', 'fr'];
const emptyI18n = (): I18nText => ({ 'pt-BR': '', en: '', es: '', fr: '' });

interface Category { id: string; name: I18nText; position: number }
interface Item {
  id: string; category_id: string | null; name: I18nText; description: I18nText; price_cents: number;
  is_available: boolean; position: number; station: string;
  schedule_enabled: boolean; schedule_days: number[]; schedule_start: string | null; schedule_end: string | null;
  is_featured: boolean; featured_label: I18nText; hide_when_off: boolean;
}

const DOW = [0, 1, 2, 3, 4, 5, 6] as const;

export function MenuPage() {
  const { t } = useTranslation();
  const { restaurants, loading } = useMyRestaurants();
  const restaurant = restaurants[0];
  const locale = restaurant?.locale ?? 'pt-BR';
  const currency = restaurant?.currency ?? 'BRL';

  const [categories, setCategories] = useState<Category[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [catPick, setCatPick] = useState('');

  const [itemId, setItemId] = useState<string | null>(null);
  const [itemName, setItemName] = useState('');
  const [itemDesc, setItemDesc] = useState<I18nText>(emptyI18n());
  const [itemPrice, setItemPrice] = useState('');
  const [itemCat, setItemCat] = useState('');
  const [itemStation, setItemStation] = useState('cozinha');
  const [translating, setTranslating] = useState(false);
  const [schedEnabled, setSchedEnabled] = useState(false);
  const [schedDays, setSchedDays] = useState<number[]>([]);
  const [schedStart, setSchedStart] = useState('');
  const [schedEnd, setSchedEnd] = useState('');
  const [featured, setFeatured] = useState(false);
  const [featuredLabel, setFeaturedLabel] = useState('');
  const [hideOff, setHideOff] = useState(false);

  const reload = useCallback(async () => {
    if (!restaurant) return;
    const { data: cats } = await supabase.from('menu_categories').select('id, name, position').eq('restaurant_id', restaurant.id).order('position');
    const { data: its } = await supabase.from('menu_items')
      .select('id, category_id, name, description, price_cents, is_available, position, station, schedule_enabled, schedule_days, schedule_start, schedule_end, is_featured, featured_label, hide_when_off')
      .eq('restaurant_id', restaurant.id).order('position');
    if (cats) setCategories(cats as Category[]);
    if (its) setItems(its as Item[]);
  }, [restaurant]);

  useEffect(() => { void reload(); }, [reload]);

  // categorias do banco ainda não adicionadas (compara pelo nome pt-BR)
  const usedPt = new Set(categories.map((c) => c.name['pt-BR']));
  const available = CATEGORY_BANK.filter((d) => !usedPt.has(d['pt-BR']));

  async function addCategory() {
    if (!restaurant || !catPick) return;
    const def = CATEGORY_BANK.find((d) => d.key === catPick);
    if (!def) return;
    const { error: err } = await supabase.from('menu_categories').insert({ restaurant_id: restaurant.id, name: categoryName(def), position: categories.length + 1 });
    if (err) return setError(err.message);
    setCatPick('');
    void reload();
  }
  async function deleteCategory(id: string) { await supabase.from('menu_categories').delete().eq('id', id); void reload(); }

  function editItem(it: Item) {
    setItemId(it.id);
    setItemName(pickLocale(it.name, locale) || Object.values(it.name ?? {})[0] || '');
    setItemDesc({ ...emptyI18n(), ...(it.description ?? {}) });
    setItemPrice((it.price_cents / 100).toString());
    setItemCat(it.category_id ?? '');
    setItemStation(it.station ?? 'cozinha');
    setSchedEnabled(Boolean(it.schedule_enabled));
    setSchedDays(it.schedule_days ?? []);
    setSchedStart((it.schedule_start ?? '').slice(0, 5));
    setSchedEnd((it.schedule_end ?? '').slice(0, 5));
    setFeatured(Boolean(it.is_featured));
    setFeaturedLabel(pickLocale(it.featured_label ?? {}, locale) || '');
    setHideOff(Boolean(it.hide_when_off));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }
  function resetItemForm() {
    setItemId(null); setItemName(''); setItemDesc(emptyI18n()); setItemPrice(''); setItemCat(''); setItemStation('cozinha');
    setSchedEnabled(false); setSchedDays([]); setSchedStart(''); setSchedEnd(''); setFeatured(false); setFeaturedLabel(''); setHideOff(false);
  }
  function toggleDay(d: number) { setSchedDays((p) => (p.includes(d) ? p.filter((x) => x !== d) : [...p, d].sort())); }

  async function translateDesc() {
    const src = itemDesc[locale]?.trim();
    if (!src) return;
    setTranslating(true);
    const targets = ALL_LANGS.filter((l) => l !== locale);
    const { data } = await supabase.functions.invoke('translate', { body: { text: src, source: locale, targets } });
    setTranslating(false);
    const tr = (data as { translations?: Record<string, string> } | null)?.translations ?? {};
    setItemDesc((prev) => ({ ...prev, ...tr }));
  }

  async function saveItem(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    if (!restaurant) return;
    const payload = {
      restaurant_id: restaurant.id,
      category_id: itemCat || null,
      name: { [locale]: itemName.trim() },
      description: { ...itemDesc, [locale]: (itemDesc[locale] ?? '').trim() },
      price_cents: parseMoneyToCents(itemPrice),
      station: itemStation,
      schedule_enabled: schedEnabled,
      schedule_days: schedEnabled ? schedDays : [],
      schedule_start: schedEnabled && schedStart ? schedStart : null,
      schedule_end: schedEnabled && schedEnd ? schedEnd : null,
      is_featured: featured,
      featured_label: featured && featuredLabel.trim() ? { [locale]: featuredLabel.trim() } : {},
      hide_when_off: schedEnabled ? hideOff : false
    };
    const { error: err } = itemId
      ? await supabase.from('menu_items').update(payload).eq('id', itemId)
      : await supabase.from('menu_items').insert({ ...payload, position: items.length + 1 });
    if (err) return setError(err.message);
    resetItemForm();
    void reload();
  }
  async function deleteItem(id: string) { await supabase.from('menu_items').delete().eq('id', id); void reload(); }
  async function toggleAvailable(it: Item) { await supabase.from('menu_items').update({ is_available: !it.is_available }).eq('id', it.id); void reload(); }

  if (loading || !restaurant) return <div className="center-note">{t('common.loading')}</div>;

  return (
    <div className="page">
      <h1 className="page__title">{t('menu.title')}</h1>

      {/* Categorias (banco padronizado) */}
      <h2 className="section-title">{t('menu.categories')}</h2>
      <ul className="list">
        {categories.map((c) => (
          <li key={c.id} className="list__row">
            <span>{pickLocale(c.name, locale)}</span>
            <button type="button" className="link-button" onClick={() => void deleteCategory(c.id)}>{t('menu.delete')}</button>
          </li>
        ))}
        {categories.length === 0 && <li className="list__row muted-note">{t('menu.no_categories')}</li>}
      </ul>
      <div className="form-row">
        <select value={catPick} onChange={(e) => setCatPick(e.target.value)}>
          <option value="">{t('menu.pick_category')}</option>
          {available.map((d) => (<option key={d.key} value={d.key}>{d[locale as keyof typeof d] ?? d['pt-BR']}</option>))}
        </select>
        <button type="button" className="btn btn--ghost btn--sm" disabled={!catPick} onClick={() => void addCategory()}>{t('menu.add_category')}</button>
      </div>

      {/* Form de item */}
      <h2 className="section-title">{itemId ? t('menu.edit_item') : t('menu.new_item')}</h2>
      <form onSubmit={saveItem} className="form">
        <label className="field">
          <span>{t('menu.name')}</span>
          <input value={itemName} onChange={(e) => setItemName(e.target.value)} required />
        </label>

        <label className="field">
          <span>{t('menu.description')}</span>
          <textarea rows={3} value={itemDesc[locale] ?? ''} onChange={(e) => setItemDesc((p) => ({ ...p, [locale]: e.target.value }))} required
            style={{ fontFamily: 'var(--font-sans)', fontSize: 15, padding: '10px 12px', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', background: 'var(--color-creme)', color: 'var(--color-carvao)' }} />
        </label>
        <button type="button" className="btn btn--ghost btn--sm" style={{ alignSelf: 'flex-start' }} disabled={translating || !itemDesc[locale]?.trim()} onClick={() => void translateDesc()}>
          {translating ? '…' : `🌐 ${t('menu.translate_auto')}`}
        </button>

        <div className="form-row">
          <label className="field">
            <span>{t('menu.price')} ({currency})</span>
            <input value={itemPrice} inputMode="decimal" required onChange={(e) => setItemPrice(e.target.value.replace(/[^0-9.,]/g, ''))} />
          </label>
          <label className="field">
            <span>{t('menu.category')}</span>
            <select value={itemCat} onChange={(e) => setItemCat(e.target.value)}>
              <option value="">{t('menu.uncategorized')}</option>
              {categories.map((c) => (<option key={c.id} value={c.id}>{pickLocale(c.name, locale)}</option>))}
            </select>
          </label>
          <label className="field">
            <span>{t('menu.station')}</span>
            <select value={itemStation} onChange={(e) => setItemStation(e.target.value)}>
              <option value="cozinha">{t('menu.station_kitchen')}</option>
              <option value="bar">{t('menu.station_bar')}</option>
            </select>
          </label>
        </div>

        <label className="adj">
          <input type="checkbox" checked={schedEnabled} onChange={(e) => setSchedEnabled(e.target.checked)} />
          🕒 {t('menu.schedule_enabled')}
        </label>
        {schedEnabled && (
          <div className="schedule-box">
            <div className="day-toggles">
              {DOW.map((d) => (
                <button key={d} type="button" className={`day-chip${schedDays.includes(d) ? ' day-chip--on' : ''}`} onClick={() => toggleDay(d)}>{t(`menu.dow_${d}`)}</button>
              ))}
            </div>
            <div className="form-row">
              <label className="field"><span>{t('menu.time_start')}</span><input type="time" value={schedStart} onChange={(e) => setSchedStart(e.target.value)} /></label>
              <label className="field"><span>{t('menu.time_end')}</span><input type="time" value={schedEnd} onChange={(e) => setSchedEnd(e.target.value)} /></label>
            </div>
            <label className="adj"><input type="checkbox" checked={hideOff} onChange={(e) => setHideOff(e.target.checked)} />{t('menu.hide_when_off')}</label>
            <span className="muted-note">{t('menu.schedule_hint')}</span>
          </div>
        )}
        <label className="adj">
          <input type="checkbox" checked={featured} onChange={(e) => setFeatured(e.target.checked)} />
          ⭐ {t('menu.featured')}
        </label>
        {featured && (
          <input className="onb-input" placeholder={t('menu.featured_label')} value={featuredLabel} onChange={(e) => setFeaturedLabel(e.target.value)} />
        )}

        {error && <p className="form-error">{error}</p>}
        <div className="form-row">
          <button type="submit" className="btn btn--primary">{t('menu.save')}</button>
          {itemId && <button type="button" className="btn btn--ghost" onClick={resetItemForm}>{t('menu.cancel')}</button>}
        </div>
      </form>

      {/* Itens */}
      <h2 className="section-title">{t('menu.items')}</h2>
      {items.length === 0 ? (
        <p className="muted-note">{t('menu.no_items')}</p>
      ) : (
        <ul className="list">
          {items.map((it) => (
            <li key={it.id} className="list__row">
              <span className={it.is_available ? '' : 'dimmed'}>
                {pickLocale(it.name, locale)} · {formatMoney(it.price_cents, currency, locale)}
                {!it.is_available && <em className="tag"> ({t('menu.unavailable')})</em>}
                {it.schedule_enabled && <em className="tag"> 🕒 {t('menu.scheduled_badge')}</em>}
                {it.is_featured && <em className="tag"> ⭐</em>}
              </span>
              <span className="row-actions">
                <button type="button" className="link-button" onClick={() => void toggleAvailable(it)}>{it.is_available ? t('menu.mark_unavailable') : t('menu.mark_available')}</button>
                <button type="button" className="link-button" onClick={() => editItem(it)}>{t('menu.edit_item')}</button>
                <button type="button" className="link-button" onClick={() => void deleteItem(it.id)}>{t('menu.delete')}</button>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
